import React, { useEffect, useState } from 'react';
import { Plus, Save, Trash2, Loader2, ArrowUp, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import UploadField from './UploadField';
import RichTextEditor from './RichTextEditor';

const inp = 'w-full bg-navy-dark border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-pink-brand';
const lbl = 'text-white/60 text-[10px] font-bold uppercase tracking-widest mb-1 block';

function Card({ children }) { return <div className="glass-card p-5">{children}</div>; }
function Loader() { return <div className="text-center py-8"><Loader2 className="text-pink-brand animate-spin mx-auto" /></div>; }

// =================== CHI SIAMO ===================
export function ChiSiamoEditor() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.getChiSiamo().then((d) => {
      setData({
        subtitle: d.subtitle || '',
        paragraphs: d.paragraphs || [],
        comitato: { nome: d.comitato?.nome || '', membri: d.comitato?.membri || [] },
        missione: d.missione || [],
        ringraziamenti: {
          autorita_html: d.ringraziamenti?.autorita_html || '',
          partner_html: d.ringraziamenti?.partner_html || '',
          operativi_html: d.ringraziamenti?.operativi_html || '',
          titolo_autorita: d.ringraziamenti?.titolo_autorita || 'Autorità & Istituzioni',
          titolo_partner: d.ringraziamenti?.titolo_partner || 'Partner Principali',
          titolo_operativi: d.ringraziamenti?.titolo_operativi || 'Staff Operativo',
        },
      });
    }).catch(() => setData({
      subtitle: '', paragraphs: [], comitato: { nome: '', membri: [] }, missione: [],
      ringraziamenti: { autorita_html: '', partner_html: '', operativi_html: '', titolo_autorita: 'Autorità & Istituzioni', titolo_partner: 'Partner Principali', titolo_operativi: 'Staff Operativo' },
    }));
  }, []);
  if (!data) return <Loader />;
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setComitato = (k, v) => setData((d) => ({ ...d, comitato: { ...(d.comitato || {}), [k]: v } }));
  const setRing = (k, v) => setData((d) => ({ ...d, ringraziamenti: { ...(d.ringraziamenti || {}), [k]: v } }));
  const updateMembro = (i, k, v) => {
    const next = [...data.comitato.membri]; next[i] = { ...next[i], [k]: v };
    setComitato('membri', next);
  };
  const updateMissione = (i, k, v) => {
    const next = [...data.missione]; next[i] = { ...next[i], [k]: v };
    set('missione', next);
  };

  const save = async () => {
    setSaving(true);
    try { await api.saveChiSiamo(data); toast.success('Chi siamo aggiornato'); }
    catch (e) { toast.error(e?.message || 'Errore salvataggio'); }
    setSaving(false);
  };

  const ICONS = ['Trophy', 'Heart', 'Users', 'Award', 'Star', 'Flag', 'Shield', 'Target', 'Zap', 'Calendar', 'MapPin', 'Camera'];

  return (
    <div className="space-y-4" data-testid="chi-siamo-editor">
      <Card>
        <h3 className="text-white font-bold mb-3">Intestazione</h3>
        <label className={lbl}>Sottotitolo (es. nome società sotto al titolo "Chi Siamo")</label>
        <input className={inp} value={data.subtitle} onChange={(e) => set('subtitle', e.target.value)} placeholder="A.S.D.P. Primavera Cavese" />
      </Card>

      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-bold">Paragrafi descrittivi</h3>
          <button className="btn-primary !py-2 !px-4 !text-xs" onClick={() => set('paragraphs', [...data.paragraphs, ''])}><Plus size={12} /> Aggiungi paragrafo</button>
        </div>
        <div className="space-y-3">
          {data.paragraphs.map((p, i) => (
            <div key={i} className="bg-navy-dark border border-white/10 rounded-lg p-3 flex gap-2 items-start">
              <span className="text-pink-brand font-bold w-6 text-sm shrink-0">{i + 1}</span>
              <textarea rows={3} value={p} onChange={(e) => {
                const next = [...data.paragraphs]; next[i] = e.target.value; set('paragraphs', next);
              }} className={inp + ' flex-1'} />
              <button onClick={() => set('paragraphs', data.paragraphs.filter((_, idx) => idx !== i))} className="text-white/60 hover:text-red-400 p-1"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-bold">"La nostra missione" (card della pagina)</h3>
          <button className="btn-primary !py-2 !px-4 !text-xs" onClick={() => set('missione', [...data.missione, { icon: 'Trophy', titolo: '', desc: '' }])}><Plus size={12} /> Aggiungi card</button>
        </div>
        <div className="space-y-3">
          {data.missione.map((m, i) => (
            <div key={i} className="bg-navy-dark border border-white/10 rounded-lg p-3">
              <div className="grid sm:grid-cols-12 gap-2 items-end">
                <div className="sm:col-span-2">
                  <label className={lbl}>Icona</label>
                  <select className={inp} value={m.icon || 'Trophy'} onChange={(e) => updateMissione(i, 'icon', e.target.value)}>
                    {ICONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-4"><label className={lbl}>Titolo</label><input className={inp} value={m.titolo || ''} onChange={(e) => updateMissione(i, 'titolo', e.target.value)} /></div>
                <div className="sm:col-span-5"><label className={lbl}>Descrizione</label><input className={inp} value={m.desc || ''} onChange={(e) => updateMissione(i, 'desc', e.target.value)} /></div>
                <div className="sm:col-span-1">
                  <button onClick={() => set('missione', data.missione.filter((_, idx) => idx !== i))} className="text-white/60 hover:text-red-400 p-2"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
          {data.missione.length === 0 && <p className="text-white/50 text-sm text-center py-4">Nessuna card. Clicca "Aggiungi card" per crearne una.</p>}
        </div>
      </Card>

      <Card>
        <h3 className="text-white font-bold mb-3">Comitato organizzatore</h3>
        <label className={lbl}>Nome società/comitato</label>
        <input className={inp} value={data.comitato.nome || ''} onChange={(e) => setComitato('nome', e.target.value)} />
        <div className="mt-4 flex items-center justify-between mb-2">
          <span className="text-white/70 text-xs font-bold uppercase tracking-widest">Membri ({data.comitato.membri.length}) — campi separati con etichetta + valore + logo opzionale</span>
          <button onClick={() => setComitato('membri', [...data.comitato.membri, { ruolo: '', nome: '', logo: '' }])} className="text-pink-brand text-xs font-bold"><Plus size={12} className="inline" /> Aggiungi membro</button>
        </div>
        <div className="space-y-3">
          {data.comitato.membri.map((m, i) => (
            <div key={i} className="bg-navy-dark p-3 rounded-lg border border-white/10">
              <div className="grid sm:grid-cols-12 gap-2 items-start">
                <div className="sm:col-span-4">
                  <label className={lbl}>Etichetta (ruolo/titolo)</label>
                  <input className={inp} placeholder="es. Presidente" value={m.ruolo || ''} onChange={(e) => updateMembro(i, 'ruolo', e.target.value)} />
                </div>
                <div className="sm:col-span-7">
                  <label className={lbl}>Valore (nome / testo)</label>
                  <input className={inp} placeholder="Nome e cognome" value={m.nome || ''} onChange={(e) => updateMembro(i, 'nome', e.target.value)} />
                </div>
                <div className="sm:col-span-1 flex sm:justify-end pt-5">
                  <button onClick={() => setComitato('membri', data.comitato.membri.filter((_, idx) => idx !== i))} className="text-white/60 hover:text-red-400 p-2" title="Elimina membro"><Trash2 size={14} /></button>
                </div>
                <div className="sm:col-span-12 mt-2">
                  <UploadField label="Logo (opzionale, carica da PC)" accept="image/*" folder="comitato" value={m.logo || ''} onChange={(v) => updateMembro(i, 'logo', v)} />
                </div>
              </div>
            </div>
          ))}
          {data.comitato.membri.length === 0 && <p className="text-white/50 text-sm text-center py-4">Nessun membro. Clicca "Aggiungi membro".</p>}
        </div>
      </Card>

      <Card>
        <h3 className="text-white font-bold mb-3">Si Ringrazia (3 colonne)</h3>
        <p className="text-white/60 text-xs mb-3">Ogni colonna ha la barra completa di formattazione: titolo della persona in grassetto + carica più piccola sotto, allineamento, colori, immagini e link. Niente pallini automatici — formatti tu come vuoi.</p>
        <div className="grid md:grid-cols-3 gap-3">
          <RingGroupRich
            titolo={data.ringraziamenti.titolo_autorita}
            html={data.ringraziamenti.autorita_html}
            onTitle={(v) => setRing('titolo_autorita', v)}
            onHtml={(v) => setRing('autorita_html', v)}
            placeholder="Es. <strong>Famiglia Abbro</strong><br><small>Sostenitori</small>"
          />
          <RingGroupRich
            titolo={data.ringraziamenti.titolo_partner}
            html={data.ringraziamenti.partner_html}
            onTitle={(v) => setRing('titolo_partner', v)}
            onHtml={(v) => setRing('partner_html', v)}
            placeholder="Es. <strong>Giuseppe Milito</strong><br><small>Gruppo Milito</small>"
          />
          <RingGroupRich
            titolo={data.ringraziamenti.titolo_operativi}
            html={data.ringraziamenti.operativi_html}
            onTitle={(v) => setRing('titolo_operativi', v)}
            onHtml={(v) => setRing('operativi_html', v)}
            placeholder="Es. <strong>Croce Rossa</strong><br><small>Sez. Cava de' Tirreni</small>"
          />
        </div>
      </Card>

      <button onClick={save} disabled={saving} className="btn-primary" data-testid="chi-siamo-save"><Save size={14} /> {saving ? 'Salvo…' : 'Salva tutto'}</button>
    </div>
  );
}

function RingGroupRich({ titolo, html, onTitle, onHtml, placeholder }) {
  return (
    <div className="bg-navy-dark border border-white/10 rounded-lg p-3 space-y-2">
      <input
        className="w-full bg-navy border border-white/15 rounded-lg px-3 py-2 text-pink-brand font-bold text-xs uppercase tracking-widest focus:outline-none focus:border-pink-brand"
        value={titolo}
        onChange={(e) => onTitle(e.target.value)}
        placeholder="Titolo gruppo"
      />
      <RichTextEditor
        value={html}
        onChange={onHtml}
        placeholder={placeholder || 'Scrivi qui i nominativi…'}
        folder="ringraziamenti"
      />
      <p className="text-white/40 text-[10px]">Suggerimento: scrivi il nome in <strong>grassetto</strong>, vai a capo (Invio) e digita la carica con dimensione <em>Piccolo</em> dalla barra strumenti.</p>
    </div>
  );
}

// =================== CONTATTI ===================
export function ContattiEditor() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.getContatti().then(setData).catch(() => setData({})); }, []);
  if (!data) return <Loader />;
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const save = async () => {
    setSaving(true);
    try { await api.saveContatti(data); toast.success('Contatti aggiornati'); }
    catch (e) { toast.error(e?.message || 'Errore salvataggio'); }
    setSaving(false);
  };
  return (
    <div className="space-y-4" data-testid="contatti-editor">
      <Card>
        <h3 className="text-white font-bold mb-3">Recapiti principali</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className={lbl}>Email</label><input className={inp} value={data.email || ''} onChange={(e) => set('email', e.target.value)} data-testid="contatti-email" /></div>
          <div><label className={lbl}>Indirizzo</label><input className={inp} value={data.address || ''} onChange={(e) => set('address', e.target.value)} /></div>
          <div><label className={lbl}>WhatsApp</label><input className={inp} value={data.whatsapp || ''} onChange={(e) => set('whatsapp', e.target.value)} placeholder="+39..." /></div>
          <div><label className={lbl}>Orari</label><input className={inp} value={data.orari || ''} onChange={(e) => set('orari', e.target.value)} /></div>
        </div>
        <div className="mt-3">
          <label className={lbl}>Telefoni (uno per riga)</label>
          <textarea rows={3} className={inp} value={(data.phones || []).join('\n')} onChange={(e) => set('phones', e.target.value.split('\n').map((s) => s.trim()).filter(Boolean))} />
        </div>
      </Card>
      <Card>
        <h3 className="text-white font-bold mb-3">Social</h3>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className={lbl}>Facebook URL</label><input className={inp} value={data.facebook_url || ''} onChange={(e) => set('facebook_url', e.target.value)} /></div>
          <div><label className={lbl}>Instagram URL</label><input className={inp} value={data.instagram_url || ''} onChange={(e) => set('instagram_url', e.target.value)} /></div>
        </div>
      </Card>
      <Card>
        <h3 className="text-white font-bold mb-3">Descrizione pagina contatti</h3>
        <textarea rows={4} className={inp} value={data.descrizione || ''} onChange={(e) => set('descrizione', e.target.value)} />
      </Card>
      <button onClick={save} disabled={saving} className="btn-primary" data-testid="contatti-save"><Save size={14} /> {saving ? 'Salvo…' : 'Salva tutto'}</button>
    </div>
  );
}

// =================== CALENDARIO ===================
export function CalendarioEditor() {
  const [items, setItems] = useState(null);
  const [showAll, setShowAll] = useState(true);
  const [savingPref, setSavingPref] = useState(false);
  useEffect(() => {
    api.getCalendarioFull().then(setItems).catch(() => setItems([]));
    api.getSettings().then((s) => {
      if (s && s.calendario_show_all_tab !== undefined) setShowAll(s.calendario_show_all_tab !== '0');
    }).catch(() => {});
  }, []);
  if (!items) return <Loader />;
  const reload = () => api.getCalendarioFull().then(setItems);

  const addDay = async () => {
    try { await api.saveCalendario({ data: '', giorno: '', eventi: [], sort_order: items.length }); reload(); }
    catch (e) { toast.error(e?.message); }
  };
  const remove = async (id) => {
    if (!window.confirm('Eliminare questa giornata?')) return;
    try { await api.deleteCalendario(id); reload(); } catch (e) { toast.error(e?.message); }
  };

  const toggleShowAll = async () => {
    const next = !showAll;
    setShowAll(next);
    setSavingPref(true);
    try {
      await api.updateSettings({ calendario_show_all_tab: next ? '1' : '0' });
      toast.success(`Tab "Tutte le categorie" ${next ? 'mostrata' : 'nascosta'}`);
    } catch (e) {
      toast.error(e?.message || 'Errore');
      setShowAll(!next);
    }
    setSavingPref(false);
  };

  return (
    <div className="space-y-3" data-testid="calendario-editor">
      <Card>
        <h3 className="text-white font-bold mb-2">Impostazioni pagina pubblica /calendario</h3>
        <button
          onClick={toggleShowAll}
          disabled={savingPref}
          className={`w-full px-4 py-2.5 rounded-lg border text-xs font-bold uppercase tracking-widest inline-flex items-center justify-center gap-2 transition-colors ${
            showAll ? 'bg-green-500/15 border-green-500/30 text-green-400' : 'bg-red-500/15 border-red-500/30 text-red-400'
          }`}
          data-testid="toggle-calendario-all-tab"
        >
          {showAll ? '✓ Tab "Tutte le categorie" visibile — clicca per nasconderla' : '✕ Tab "Tutte le categorie" nascosta — clicca per mostrarla'}
        </button>
        <p className="text-white/60 text-[11px] mt-2">Quando è nascosta, l'utente vede solo i 3 tab per categoria (Pulcini 2015 / 2016 / Primi Calci).</p>
      </Card>

      <button className="btn-primary !py-2 !px-4 !text-sm" onClick={addDay}><Plus size={14} /> Aggiungi giornata</button>
      <p className="text-white/60 text-[11px] -mt-1">💡 <strong>Suggerimento:</strong> per la finale del 31 maggio, spunta tutte e 3 le categorie nella sezione "Categorie" della giornata → apparirà in tutti i tab.</p>
      {items.map((d) => <DayCard key={d.id} day={d} onSaved={reload} onRemove={() => remove(d.id)} />)}
      {items.length === 0 && <Card><p className="text-white/60 text-sm text-center py-4">Nessuna giornata. Clicca "Aggiungi giornata".</p></Card>}
    </div>
  );
}
function DayCard({ day, onSaved, onRemove }) {
  const [d, setD] = useState({ ...day, eventi: day.eventi || [] });
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const updEv = (i, k, v) => { const next = [...d.eventi]; next[i] = { ...next[i], [k]: v }; set('eventi', next); };
  const addEv = () => set('eventi', [...d.eventi, { ora: '', evento: '', luogo: '' }]);
  const rmEv = (i) => set('eventi', d.eventi.filter((_, idx) => idx !== i));
  const save = async () => {
    try { await api.saveCalendario(d); toast.success('Giornata salvata'); onSaved(); }
    catch (e) { toast.error(e?.message); }
  };
  return (
    <Card>
      <div className="grid sm:grid-cols-12 gap-3 mb-3">
        <div className="sm:col-span-2"><label className={lbl}>Giorno</label><input className={inp} value={d.giorno || ''} onChange={(e) => set('giorno', e.target.value)} placeholder="Lunedì" /></div>
        <div className="sm:col-span-4"><label className={lbl}>Data</label><input className={inp} value={d.data || ''} onChange={(e) => set('data', e.target.value)} placeholder="25 Maggio 2026" /></div>
        <div className="sm:col-span-4"><label className={lbl}>Categorie (puoi sceglierne più di una)</label>
          <div className="flex flex-wrap gap-2 text-xs">
            {[
              { v: 'pulcini-2015', l: 'Pulcini 2015' },
              { v: 'pulcini-2016', l: 'Pulcini 2016' },
              { v: 'primi-calci', l: 'Primi Calci 2017/18' },
            ].map((opt) => {
              const arr = (d.categoria || '').split(',').map((s) => s.trim()).filter(Boolean);
              const checked = arr.includes(opt.v);
              return (
                <label key={opt.v} className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-lg border cursor-pointer ${checked ? 'bg-pink-brand text-white border-pink-brand' : 'bg-navy-dark text-white/70 border-white/15'}`}>
                  <input
                    type="checkbox"
                    className="accent-pink-brand"
                    checked={checked}
                    onChange={(e) => {
                      const next = e.target.checked ? [...arr, opt.v] : arr.filter((x) => x !== opt.v);
                      set('categoria', next.join(','));
                    }}
                  />
                  <span className="text-[11px] font-semibold">{opt.l}</span>
                </label>
              );
            })}
          </div>
          <p className="text-white/40 text-[10px] mt-1">Lascia vuoto per auto-rilevamento dal testo.</p>
        </div>
        <div className="sm:col-span-1"><label className={lbl}>Etichetta</label><input className={inp} value={d.etichetta || ''} onChange={(e) => set('etichetta', e.target.value)} placeholder="Finale" /></div>
        <div className="sm:col-span-1"><label className={lbl}>Ordine</label><input type="number" className={inp} value={d.sort_order || 0} onChange={(e) => set('sort_order', parseInt(e.target.value, 10) || 0)} /></div>
      </div>
      <div className="border-t border-white/10 pt-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-white/70 text-xs font-bold uppercase tracking-widest">Eventi della giornata ({d.eventi.length})</span>
          <button onClick={addEv} className="text-pink-brand text-xs font-bold"><Plus size={12} className="inline" /> Aggiungi</button>
        </div>
        {d.eventi.map((ev, i) => (
          <div key={i} className="grid sm:grid-cols-12 gap-2 items-center bg-navy-dark p-2 rounded">
            <input className={inp + ' sm:col-span-2'} placeholder="Ora" value={ev.ora || ''} onChange={(e) => updEv(i, 'ora', e.target.value)} />
            <input className={inp + ' sm:col-span-6'} placeholder="Evento" value={ev.evento || ''} onChange={(e) => updEv(i, 'evento', e.target.value)} />
            <input className={inp + ' sm:col-span-3'} placeholder="Luogo" value={ev.luogo || ''} onChange={(e) => updEv(i, 'luogo', e.target.value)} />
            <button onClick={() => rmEv(i)} className="text-white/60 hover:text-red-400 sm:col-span-1"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      <div className="mt-3 flex gap-2">
        <button onClick={save} className="btn-primary !py-2 !px-4 !text-xs"><Save size={12} /> Salva giornata</button>
        <button onClick={onRemove} className="px-4 py-2 rounded-full bg-red-500/15 border border-red-500/30 text-red-400 text-xs font-bold uppercase">Elimina</button>
      </div>
    </Card>
  );
}

// =================== EDIZIONI ===================
export function EdizioniEditor() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.getEditions().then(setItems).catch(() => setItems([])); }, []);
  if (!items) return <Loader />;
  const reload = () => api.getEditions().then(setItems);
  const addNew = async () => {
    try { await api.saveEdition({ anno: new Date().getFullYear(), edizione: '', date: '', squadrePartecipanti: 0, copertina: '', descrizione: '', highlights: [], vincitoriCategorie: [] }); reload(); }
    catch (e) { toast.error(e?.message); }
  };
  const remove = async (id) => {
    if (!window.confirm('Eliminare questa edizione?')) return;
    try { await api.deleteEdition(id); reload(); } catch (e) { toast.error(e?.message); }
  };
  return (
    <div className="space-y-3" data-testid="edizioni-editor">
      <button className="btn-primary !py-2 !px-4 !text-sm" onClick={addNew}><Plus size={14} /> Nuova edizione</button>
      {items.map((ed) => <EdRow key={ed.id} ed={ed} onSaved={reload} onRemove={() => remove(ed.id)} />)}
      {items.length === 0 && <Card><p className="text-white/60 text-sm text-center py-4">Nessuna edizione passata salvata.</p></Card>}
    </div>
  );
}
function EdRow({ ed, onSaved, onRemove }) {
  const [d, setD] = useState({ ...ed, highlights: ed.highlights || [], vincitoriCategorie: ed.vincitoriCategorie || [], gallery: ed.gallery || [] });
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try { await api.saveEdition(d); toast.success('Edizione salvata'); onSaved(); }
    catch (e) { toast.error(e?.message); }
  };
  const updWin = (i, k, v) => { const n = [...d.vincitoriCategorie]; n[i] = { ...n[i], [k]: v }; set('vincitoriCategorie', n); };
  const addImg = (src) => { if (src) set('gallery', [...d.gallery, { src }]); };
  const addManyImgs = (urls) => { if (Array.isArray(urls) && urls.length) set('gallery', [...d.gallery, ...urls.map((src) => ({ src }))]); };
  const moveImg = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= d.gallery.length) return;
    const n = [...d.gallery]; [n[i], n[j]] = [n[j], n[i]]; set('gallery', n);
  };
  const rmImg = (i) => set('gallery', d.gallery.filter((_, idx) => idx !== i));
  return (
    <Card>
      <div className="grid sm:grid-cols-4 gap-3">
        <div><label className={lbl}>Anno</label><input type="number" className={inp} value={d.anno || ''} onChange={(e) => set('anno', parseInt(e.target.value, 10) || 0)} /></div>
        <div className="sm:col-span-2"><label className={lbl}>Edizione</label><input className={inp} value={d.edizione || ''} onChange={(e) => set('edizione', e.target.value)} placeholder="XIII Memorial" /></div>
        <div><label className={lbl}>Squadre</label><input type="number" className={inp} value={d.squadrePartecipanti || 0} onChange={(e) => set('squadrePartecipanti', parseInt(e.target.value, 10) || 0)} /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        <div><label className={lbl}>Date</label><input className={inp} value={d.date || ''} onChange={(e) => set('date', e.target.value)} placeholder="25-31 Maggio 2025" /></div>
        <UploadField label="Copertina (carica da PC oppure URL)" accept="image/*" folder="edizioni" value={d.copertina} onChange={(v) => set('copertina', v)} />
      </div>
      <div className="mt-3"><label className={lbl}>Descrizione</label><textarea rows={3} className={inp} value={d.descrizione || ''} onChange={(e) => set('descrizione', e.target.value)} /></div>
      <div className="mt-3"><label className={lbl}>Highlights (uno per riga)</label>
        <textarea rows={3} className={inp} value={(d.highlights || []).join('\n')} onChange={(e) => set('highlights', e.target.value.split('\n').filter(Boolean))} />
      </div>
      <div className="mt-3 border-t border-white/10 pt-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-white/70 text-xs font-bold uppercase tracking-widest">Vincitori per categoria</span>
          <button onClick={() => set('vincitoriCategorie', [...d.vincitoriCategorie, { categoria: '', vincitore: '' }])} className="text-pink-brand text-xs font-bold"><Plus size={12} className="inline" /> Aggiungi</button>
        </div>
        {d.vincitoriCategorie.map((v, i) => (
          <div key={i} className="grid sm:grid-cols-12 gap-2 mb-2 items-center bg-navy-dark p-2 rounded">
            <input className={inp + ' sm:col-span-5'} placeholder="Categoria" value={v.categoria || ''} onChange={(e) => updWin(i, 'categoria', e.target.value)} />
            <input className={inp + ' sm:col-span-6'} placeholder="Squadra vincitrice" value={v.vincitore || ''} onChange={(e) => updWin(i, 'vincitore', e.target.value)} />
            <button onClick={() => set('vincitoriCategorie', d.vincitoriCategorie.filter((_, idx) => idx !== i))} className="text-white/60 hover:text-red-400 sm:col-span-1"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
      <div className="mt-3 border-t border-white/10 pt-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-white/70 text-xs font-bold uppercase tracking-widest">Galleria foto edizione ({d.gallery.length})</span>
        </div>
        <UploadField
          label="Aggiungi foto alla galleria (selezione multipla)"
          accept="image/*"
          folder="edizioni"
          multiple
          onMultiUpload={addManyImgs}
        />
        {d.gallery.length > 0 && (
          <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
            {d.gallery.map((g, i) => (
              <div key={i} className="relative group bg-navy-dark border border-white/10 rounded-lg overflow-hidden">
                <img src={g.src} alt="" className="w-full aspect-square object-cover" />
                <div className="absolute inset-0 bg-navy-dark/85 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                  <button onClick={() => moveImg(i, -1)} disabled={i === 0} className="w-7 h-7 rounded bg-pink-brand/80 text-white text-xs font-bold disabled:opacity-30">←</button>
                  <button onClick={() => moveImg(i, 1)} disabled={i === d.gallery.length - 1} className="w-7 h-7 rounded bg-pink-brand/80 text-white text-xs font-bold disabled:opacity-30">→</button>
                  <button onClick={() => rmImg(i)} className="w-7 h-7 rounded bg-red-500/90 text-white"><Trash2 size={12} className="inline" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-3 flex gap-2">
        <button onClick={save} className="btn-primary !py-2 !px-4 !text-xs"><Save size={12} /> Salva edizione</button>
        <button onClick={onRemove} className="px-4 py-2 rounded-full bg-red-500/15 border border-red-500/30 text-red-400 text-xs font-bold uppercase">Elimina</button>
      </div>
    </Card>
  );
}
